import React, { useMemo } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import AddToCart from '@components/shared/AddToCart';
import { ProductCode, BundleProductData } from '@contracts';
import { getProductTitle } from '@data/products';
import { getShopProducts } from '@data/storefront';
import { useGeneratedCartItem } from '@utils/useGeneratedCartItem';
import Badge from '../Badge';
import Colors from '../Colors';
import { StyledSimpleBoxWrapper } from '../SharedStyles';
import { TextVariant, Typography } from './Typography';

export interface BundleBoxProps extends React.HTMLAttributes<HTMLDivElement> {
  bundleData: BundleProductData;
}

const BundleBox: React.FC<BundleBoxProps> = ({ bundleData }) => {
  const { t } = useTranslation();
  const shopProducts = getShopProducts();

  const matchingProduct = useMemo(() => {
    return shopProducts?.find((product) => product.product_code === bundleData?.bundle_product_code);
  }, [bundleData?.bundle_product_code, shopProducts]);

  const cartItem = useGeneratedCartItem(bundleData?.bundle_product_code as ProductCode);

  if (!cartItem) {
    return null;
  }

  const productName = getProductTitle(bundleData.bundle_product_code as ProductCode);
  const imageUrl = bundleData.bundle_image?.url ?? matchingProduct?.product_image?.url;
  const productDescription = bundleData.bundle_description?.text ?? matchingProduct?.product_description?.text;
  const badgeText = bundleData.badge_text?.text;

  return (
    <BoxContainer>
      {badgeText && (
        <BadgeWrapper>
          <Badge text={badgeText} type="recommended small" />
        </BadgeWrapper>
      )}

      <ContentWrapper className="simple-box-content">
        <TextWrapper>
          <Typography $variant={TextVariant.BodyBoldMD}>{productName}</Typography>
          <Typography $variant={TextVariant.BodyRegularSM}>{productDescription}</Typography>
          <AddToCartStyled cartItem={cartItem} buttonText={t('components.add-to-cart')} />
        </TextWrapper>
        <ProductImage src={imageUrl} alt={productName} />
      </ContentWrapper>
    </BoxContainer>
  );
};

const BoxContainer = styled(StyledSimpleBoxWrapper)`
  padding: 16px;
  position: relative;
  border-radius: 12px;
  border: 1px solid var(--Pale-gold, #f9d07c);
  background: var(--pale-gold-25, #fdf3de);
`;

const BadgeWrapper = styled.div<{ $selected?: boolean }>`
  display: flex;
  position: absolute;
  top: -8px;
  right: 16px;

  > p {
    color: ${Colors.navyBlue};
  }
`;

const ContentWrapper = styled.div`
  background: var(--pale-gold-25, #fdf3de);
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  justify-content: space-between;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  align-self: stretch;
  text-align: left;
`;

const AddToCartStyled = styled(AddToCart)`
  margin-top: 8px;
  padding: 8px;
  border-radius: 2px;
  height: 32px;
  font-size: 12px;
`;

const ProductImage = styled.img`
  border-radius: 8px;
  height: 106px;
  width: 106px;
`;

export default BundleBox;
